import { normalizeErrorString } from '@medplum/core';
import type { Consent, ContactPoint, Coverage, Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { CONSENT_DEFINITIONS, findConsentForDefinition, questionnaireResponseToValues } from '@polarissleep/shared';
import { EXTENSION_URLS, LOINC_SYSTEM } from '../../lib/constants';

export {
  countMissingRequiredConsents,
  findConsentForDefinition,
  isConsentActive,
} from '@polarissleep/shared';

const FHIR_PATH_TO_FIELD: Record<string, string> = {
  'Patient.name': 'name',
  'Patient.name[0].given[0]': 'first name',
  'Patient.name[0].family': 'last name',
  'Patient.telecom': 'contact information',
  'Patient.birthDate': 'date of birth',
  'Patient.address': 'address',
  'Patient.address[0].postalCode': 'zipcode',
  'Patient.address[0].city': 'city',
  'Patient.address[0].state': 'state',
  'Coverage.subscriberId': 'member ID',
};

/**
 * Converts a FHIR error message into a user-friendly string.
 * Falls back to the raw message if no mapping is found.
 * @param error - The error to convert.
 * @returns A user-friendly string representation of the error.
 */
export function friendlyErrorMessage(error: unknown): string {
  const raw = normalizeErrorString(error);

  // Match patterns like "Some validation message (Patient.name[0].given[0])"
  const match = raw.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (match) {
    const [, message, fhirPath] = match;
    const fieldName = FHIR_PATH_TO_FIELD[fhirPath];
    if (fieldName) {
      return `${message} (${fieldName})`;
    }
  }

  return raw;
}

/**
 * Strips surrounding double-quote characters from a string value (e.g. `"10505"` → `10505`).
 * @param value - The string to strip surrounding quotes from.
 * @returns The string with surrounding double-quote characters removed, or the original string if none are present.
 */
export function stripSurroundingQuotes(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1);
  }
  return value;
}

export function getTelecomValue(telecom: ContactPoint[] | undefined, system: ContactPoint['system']): string {
  return telecom?.find((t) => t.system === system)?.value ?? '';
}

export function setTelecomValue(
  telecom: ContactPoint[] | undefined,
  system: ContactPoint['system'],
  value: string
): ContactPoint[] {
  const existing = [...(telecom ?? [])];
  const index = existing.findIndex((t) => t.system === system);
  if (index >= 0) {
    existing[index] = { ...existing[index], value };
  } else {
    existing.push({ system, value });
  }
  return existing;
}

export function getBirthSex(patient: Patient): string {
  const ext = patient.extension?.find((e) => e.url === EXTENSION_URLS.birthSex);
  // The intake form stores birth sex as valueCoding; support both formats for reading
  return ext?.valueCoding?.code ?? ext?.valueCode ?? '';
}

export function setBirthSex(patient: Patient, code: string, system: string, display: string): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== EXTENSION_URLS.birthSex);
  if (code) {
    // Store as valueCoding to match the intake form (addExtension with 'valueCoding')
    extensions.push({
      url: EXTENSION_URLS.birthSex,
      valueCoding: { system, code, display },
    });
  }
  return { ...patient, extension: extensions };
}

export function getGenderIdentity(patient: Patient): string {
  // Check for us-core-genderIdentity extension first (valueCoding), fall back to Patient.gender
  const ext = patient.extension?.find((e) => e.url === EXTENSION_URLS.genderIdentity);
  return ext?.valueCoding?.code ?? patient.gender ?? '';
}

export function setGenderIdentity(
  patient: Patient,
  fhirGender: string,
  code: string,
  display: string,
  system: string
): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== EXTENSION_URLS.genderIdentity);
  if (code) {
    extensions.push({
      url: EXTENSION_URLS.genderIdentity,
      valueCoding: { system, code, display },
    });
  }
  return {
    ...patient,
    gender: (fhirGender as Patient['gender']) || undefined,
    extension: extensions,
  };
}

export function getPronouns(patient: Patient): string[] {
  return (patient.extension ?? [])
    .filter((e) => e.url === EXTENSION_URLS.pronouns)
    .map((e) => e.valueCodeableConcept?.coding?.[0]?.code ?? '')
    .filter(Boolean);
}

export function setPronouns(patient: Patient, selections: { code: string; display: string }[]): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== EXTENSION_URLS.pronouns);
  for (const { code, display } of selections) {
    extensions.push({
      url: EXTENSION_URLS.pronouns,
      valueCodeableConcept: {
        coding: [{ system: LOINC_SYSTEM, code, display }],
      },
    });
  }
  return { ...patient, extension: extensions };
}

export function getCoverageClassValue(coverage: Coverage, type: string): string {
  return coverage.class?.find((c) => c.type?.coding?.[0]?.code === type)?.value ?? '';
}

export function getCoverageClassName(coverage: Coverage, type: string): string {
  return coverage.class?.find((c) => c.type?.coding?.[0]?.code === type)?.name ?? '';
}

export function setCoverageClass(coverage: Coverage, type: string, value: string, name?: string): Coverage {
  const classes = [...(coverage.class ?? [])];
  const index = classes.findIndex((c) => c.type?.coding?.[0]?.code === type);
  if (!value) {
    if (index >= 0) {
      classes.splice(index, 1);
    }
    return { ...coverage, class: classes };
  }
  const entry = {
    type: { coding: [{ code: type }] },
    value,
    ...(name !== undefined ? { name } : {}),
  };
  if (index >= 0) {
    classes[index] = entry;
  } else {
    classes.push(entry);
  }
  return { ...coverage, class: classes };
}

export function getCoveragePhone(coverage: Coverage): string {
  const ext = coverage.extension?.find((e) => e.url === EXTENSION_URLS.coveragePhone);
  return ext?.valueString ?? '';
}

export function setCoveragePhone(coverage: Coverage, phone: string): Coverage {
  const extensions = (coverage.extension ?? []).filter((e) => e.url !== EXTENSION_URLS.coveragePhone);
  if (phone) {
    extensions.push({ url: EXTENSION_URLS.coveragePhone, valueString: phone });
  }
  return { ...coverage, extension: extensions };
}

export function formatDate(dateString: string | undefined): string {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function mergeIntakeInformedConsent(
  consents: Consent[],
  questionnaireResponses: QuestionnaireResponse[],
  patient: Patient
): Consent[] {
  const definition = CONSENT_DEFINITIONS.find((item) => item.key === 'informed-consent');
  if (!definition) {
    return consents;
  }
  if (findConsentForDefinition(consents, definition)) {
    return consents;
  }

  const latestSignedResponse = [...questionnaireResponses]
    .sort((a, b) => (b.authored ?? '').localeCompare(a.authored ?? ''))
    .find((response) => questionnaireResponseToValues(response)['consent-signed'] === true);

  if (!latestSignedResponse) {
    return consents;
  }

  return [
    ...consents,
    {
      resourceType: 'Consent',
      patient: patient.id ? { reference: `Patient/${patient.id}` } : undefined,
      status: 'active',
      scope: definition.scope,
      category: [definition.category],
      policyRule: definition.policyRule,
      dateTime: latestSignedResponse.authored ?? new Date().toISOString(),
    },
  ];
}
